import { focusAtom } from "jotai-optics"
import { atomWithStorage } from "jotai/utils"
import { safeJSONLocalStorage } from "@/utils/safe-jotai-storage"

export enum BackgroundColor {
	white = "#ffffff",
	lightGray = "#a3a3a3",
	gray = "#525252",
	darkGray = "#171717",
	black = "#000000",
}

export type ThemeMode = "light" | "dark" | "system"

export const supportedI18nLanguages = ["en", "es-419", "pt-BR"] as const

export type I18nLanguage = (typeof supportedI18nLanguages)[number]

export const isI18nLanguage = (value: string): value is I18nLanguage =>
	supportedI18nLanguages.some((language) => language === value)
export interface SettingsState {
	adaptiveCrop: boolean
	adaptiveStraighten: boolean
	adaptivePerspective: boolean
	adaptiveMasking: boolean
	adaptiveHealing: boolean
	forwardProcessing: boolean
	applyAIAdjustments: boolean
	autoStraighten: boolean
	autoPerspective: boolean
	expressCalibration: boolean
	highlightsClipping: boolean
	shadowsClipping: boolean
	applyPresetToSuggested: boolean
	applyColorMatchToSuggested: boolean
	editorBackground: BackgroundColor
	// Clustering tuning parameters (positive = more photos clustered together, negative = less photos clustered together)
	deltaSceneThresh: number // Adjusts scene similarity threshold
	deltaTimeThresh: number // Adjusts time window threshold (in seconds)
	faceDetectorConfidenceThreshold: number
	faceParserConfidenceThreshold: number
	rfdetrPersonConfidenceThreshold: number
	showInternalStyles: boolean
	themeMode: ThemeMode
	language: I18nLanguage
	usePreferredLocale: boolean
}

const defaultSettings: SettingsState = {
	adaptiveCrop: true,
	adaptiveStraighten: true,
	adaptivePerspective: true,
	adaptiveMasking: true,
	adaptiveHealing: true,
	forwardProcessing: false,
	applyAIAdjustments: true,
	autoStraighten: true,
	autoPerspective: false,
	expressCalibration: false,
	highlightsClipping: false,
	shadowsClipping: false,
	applyPresetToSuggested: true,
	applyColorMatchToSuggested: true,
	editorBackground: BackgroundColor.darkGray,
	deltaSceneThresh: 0,
	deltaTimeThresh: 0,
	faceDetectorConfidenceThreshold: 0.55,
	faceParserConfidenceThreshold: 0,
	rfdetrPersonConfidenceThreshold: 0.5,
	showInternalStyles: false,
	themeMode: "dark",
	language: "en",
	usePreferredLocale: true,
}

// Custom storage that merges stored values with defaults to handle new properties
const settingsStorage = safeJSONLocalStorage<SettingsState>()
const mergedSettingsStorage = {
	...settingsStorage,
	getItem: (key: string, initialValue: SettingsState) => {
		const stored = settingsStorage.getItem(key, initialValue)
		return { ...defaultSettings, ...stored }
	},
}

export const settingsAtom = atomWithStorage("settings", defaultSettings, mergedSettingsStorage)

export const adaptiveCropAtom = focusAtom(settingsAtom, (optic) => optic.prop("adaptiveCrop"))
export const adaptiveStraightenAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("adaptiveStraighten"),
)
export const adaptivePerspectiveAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("adaptivePerspective"),
)
export const adaptiveMaskingAtom = focusAtom(settingsAtom, (optic) => optic.prop("adaptiveMasking"))
export const adaptiveHealingAtom = focusAtom(settingsAtom, (optic) => optic.prop("adaptiveHealing"))
export const forwardProcessingAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("forwardProcessing"),
)
export const applyAIAdjustmentsAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("applyAIAdjustments"),
)

export const autoStraightenAtom = focusAtom(settingsAtom, (optic) => optic.prop("autoStraighten"))
export const autoPerspectiveAtom = focusAtom(settingsAtom, (optic) => optic.prop("autoPerspective"))

export const highlightsClippingAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("highlightsClipping"),
)
export const shadowsClippingAtom = focusAtom(settingsAtom, (optic) => optic.prop("shadowsClipping"))

export const editorBackgroundAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("editorBackground"),
)

export const deltaSceneThreshAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("deltaSceneThresh"),
)
export const deltaTimeThreshAtom = focusAtom(settingsAtom, (optic) => optic.prop("deltaTimeThresh"))
export const faceDetectorConfidenceThresholdAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("faceDetectorConfidenceThreshold"),
)
export const faceParserConfidenceThresholdAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("faceParserConfidenceThreshold"),
)
export const rfdetrPersonConfidenceThresholdAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("rfdetrPersonConfidenceThreshold"),
)

export const showInternalStylesAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("showInternalStyles"),
)
export const themeModeAtom = focusAtom(settingsAtom, (optic) => optic.prop("themeMode"))
export const languageAtom = focusAtom(settingsAtom, (optic) => optic.prop("language"))
export const usePreferredLocaleAtom = focusAtom(settingsAtom, (optic) =>
	optic.prop("usePreferredLocale"),
)

// settingsAtom normally loads its persisted value when the atom mounts in React. But initI18n() runs before the React application is rendered.
// So we need to read the persisted value from localStorage directly to get the stored language before the React app is mounted.
export const getStoredLanguage = (): I18nLanguage => {
	const settings = mergedSettingsStorage.getItem("settings", defaultSettings)
	return isI18nLanguage(settings.language) ? settings.language : "en"
}
